import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { stocktakeService } from "../../_services";
import moment from 'moment';
import toast from '../shared/Toast';

// Import components
import CatalogueTopNav from "../new-service/CatalogueTopNav";
import MonthPicker from "./MonthPicker";
import StocktakeTable from "./StocktakeTable";
import NewStocktakeModal from "./NewStocktakeModal";
import useWindowSize from "../new-service/useWindowSize";

// Import utilities
import {
  getDefaultDateRange,
  createStocktakeParams,
  createNewStocktakeObject
} from "./StocktakeUtils";

// Import styles
import "./Stocktake.css";
import "../forms/Forms.css";
import "../new-service/newservice.css";

/**
 * Stocktake component for managing stock take records
 */
const Stocktake = (props) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  // State
  const [stocktakes, setStocktakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(
    props.location?.state?.dateRange || getDefaultDateRange()
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Navigation
  const goToPrevPage = () => {
    props.history.push('/catalogue');
  };

  const navigateToStocktakeDetail = (stocktakeId) => {
    props.history.push(`/stocktake/${stocktakeId}`, { dateRange });
  };

  // Fetch data when page or date range changes
  useEffect(() => {
    fetchStocktakeHistory();
  }, [pagination.page, dateRange]);

  /**
   * Navigate to the new stock take page
   */
  const openNewStocktakeModal = () => {
    props.history.push('/stocktake/new');
  };

  /**
   * Close the new stock take modal - kept for backward compatibility
   */
  const closeNewStocktakeModal = () => {
    setIsModalOpen(false);
  };

  /**
   * Create a new stock take
   */
  const createNewStocktake = (formData) => {
    setLoading(true);
    
    const newStocktake = createNewStocktakeObject(formData.name, formData.notes);
    
    return stocktakeService.createStocktake(newStocktake)
      .then(response => {
        console.log("Create stocktake response:", response);
        
        // Check for success in the response
        // The backend returns { statusCode: 1, responseData: { status: 1, message: "...", data: {...} } }
        if (response.data &&
            ((response.data.statusCode === 1 && response.data.responseData) ||
             (response.data.status === 1))) {
          
          // Get the stocktake ID from the response
          const stocktakeId = response.data.responseData?.data?._id || response.data.data?._id;
          
          if (stocktakeId) {
            props.history.push(`/stocktake/${stocktakeId}`);
            return Promise.resolve();
          } else {
            console.error("Error: No stocktake ID in response:", response.data);
            toast.error("Failed to create stock take: No stocktake ID in response");
            setLoading(false);
            return Promise.reject(new Error("No stocktake ID in response"));
          }
        } else {
          console.error("Error creating stocktake:", response.data);
          const errorMsg = response.data?.responseData?.message ||
                          response.data?.message ||
                          response.data?.error?.message ||
                          "Unknown error";
          toast.error("Failed to create stock take: " + errorMsg);
          setLoading(false);
          return Promise.reject(new Error(errorMsg));
        }
      })
      .catch(error => {
        console.error("Error creating stocktake:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error";
        toast.error("Failed to create stock take: " + errorMessage);
        setLoading(false);
        return Promise.reject(error);
      });
  };

  /**
   * Fetch stocktake history with filters
   */
  const fetchStocktakeHistory = () => {
    setLoading(true);
    
    const params = createStocktakeParams(pagination, dateRange);
    
    stocktakeService.getStocktakeHistory(params)
      .then(response => {
        console.log("Stocktake response:", response.data);
        
        // Check for success in the response
        if (response.data &&
            ((response.data.statusCode === 1 && response.data.responseData) ||
             (response.data.status === 1))) {
          
          // Handle both response structures
          let stocktakeData = [];
          let totalCount = 0;
          
          if (response.data.responseData) {
            // New API response structure
            stocktakeData = response.data.responseData.data.stocktakes || [];
            totalCount = response.data.responseData.data.total || 0;
          } else {
            // Old API response structure
            stocktakeData = response.data.data.stocktakes || [];
            totalCount = response.data.data.total || 0;
          }
          
          setStocktakes(stocktakeData);
          setPagination({
            ...pagination,
            total: totalCount,
            totalPages: Math.ceil(totalCount / pagination.limit)
          });
        } else {
          console.error("Error fetching stocktake history:", response.data);
          setStocktakes([]);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching stocktake history:", error);
        setStocktakes([]);
        setLoading(false);
      });
  };

  /**
   * Handle page change in pagination
   */
  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      page
    });
  };

  return (
    <div className="h_n catalogue-no-header stocktake-list-page">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          <CatalogueTopNav>
            <button className="catalogue-add-btn" onClick={openNewStocktakeModal}>
              <span>New</span>
            </button>
          </CatalogueTopNav>

          <div className="forms-main-container">
            <div className="forms-content-wrapper">
              {/* Main Card */}
              <div className="forms-table-container">
                {/* Card Header */}
                <div className="forms-card-header">
                  <div className="forms-card-title">
                    <h3>Stock Takes</h3>
                    {!loading && stocktakes.length > 0 && (
                      <span className="forms-count-badge">{pagination.total}</span>
                    )}
                  </div>
                  <div className="forms-card-actions">
                    <MonthPicker
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                    />
                  </div>
                </div>

                {/* Stocktake Table */}
                <StocktakeTable
                  stocktakes={stocktakes}
                  loading={loading}
                  openNewStocktakeModal={openNewStocktakeModal}
                  navigateToStocktakeDetail={navigateToStocktakeDetail}
                  isMobile={isMobile}
                />

                {/* Table Footer with Pagination */}
                {!loading && stocktakes.length > 0 && (
                  <div className="forms-table-footer">
                    <span className="forms-table-info">
                      Showing {stocktakes.length} of {pagination.total} stock takes
                    </span>
                    {pagination.totalPages > 1 && (
                      <div className="forms-pagination-container">
                        <button
                          className="forms-pagination-btn"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                        >
                          &#8249;
                        </button>
                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.page >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              className={`forms-pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          className="forms-pagination-btn"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.totalPages}
                        >
                          &#8250;
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Stock Take Modal - kept for backward compatibility */}
      {isModalOpen && (
        <NewStocktakeModal
          isOpen={isModalOpen}
          onClose={closeNewStocktakeModal}
          onSubmit={createNewStocktake}
        />
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default connect(mapStateToProps)(Stocktake);